import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ChatWidget from "../../components/feature/ChatWidget";
import SEOHead from "../../components/feature/SEOHead";
import { services } from "../../mocks/services";
import ServiceCard from "./components/ServiceCard";
import { teamMembers } from "../../mocks/team";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://vidalferrerassessors.es";

const serviciosJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Servicios de Asesoría Fiscal, Laboral y Seguros en Pego",
    "description": "Servicios de asesoría fiscal, laboral, contabilidad, seguros y constitución de empresas en Pego. Cada área con un especialista dedicado. Primera consulta gratuita.",
    "url": `${siteUrl}/servicios`,
    "provider": {
      "@type": "AccountingService",
      "name": "Vidal Ferrer Asesores",
      "url": siteUrl
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Servicios de Vidal Ferrer Asesores",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Asesoría Fiscal" },
      { "@type": "ListItem", "position": 2, "name": "Asesoría Laboral" },
      { "@type": "ListItem", "position": 3, "name": "Contabilidad y Finanzas" },
      { "@type": "ListItem", "position": 4, "name": "Seguros Empresariales" },
      { "@type": "ListItem", "position": 5, "name": "Constitución de Empresas" },
      { "@type": "ListItem", "position": 6, "name": "Consultoría de Gestión" }
    ]
  }
];

const specialistAreas: Record<string, string[]> = {
  "Pasqual Vidal Ferrer": ["Asesoría Fiscal", "Renta", "Constitución de Empresas"],
  "Anna Vidal Ferrer": ["Asesoría Laboral", "Consultoría de Gestión"],
  "Marilo Ferrer Bay": ["Seguros Empresariales y Administración", "Administración"],
};

export default function ServiciosPage() {
  const [activeService, setActiveService] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="Servicios · Asesoría Fiscal, Laboral y Seguros Pego | Vidal Ferrer Asesores"
        description="Asesoría fiscal, laboral, contabilidad, seguros y constitución de empresas en Pego. Especialistas dedicados a cada área. Primera consulta gratuita sin compromiso."
        keywords="asesoría fiscal Pego, asesoría laboral Pego, seguros empresariales Alicante, contabilidad autónomos Pego, constitución empresas Marina Alta"
        canonical="/servicios"
        jsonLd={serviciosJsonLd}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 bg-[#F0F4ED]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10 text-center">
            <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-4">Lo que hacemos</p>
            <h1 className="text-4xl md:text-6xl font-bold text-[#1A1A1A] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Nuestros Servicios
            </h1>
            <p className="text-[#6B6B6B] text-lg max-w-2xl mx-auto">
              Cada servicio tiene un asesor con nombre y apellido que lo gestiona. Sabrás siempre quién lleva tu caso.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  isActive={activeService === service.id}
                  onToggle={() => setActiveService(activeService === service.id ? null : service.id)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Specialists section */}
        <section className="py-20 bg-[#F8F7F4]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-3">El equipo</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Cada área, un experto
              </h2>
              <p className="text-[#6B6B6B] text-base mt-4 max-w-xl mx-auto">
                Nuestros tres especialistas se reparten las áreas de forma que siempre tienes a alguien que domina tu tema en profundidad.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {teamMembers.map((member) => {
                const areas = specialistAreas[member.name] ?? [];
                return (
                  <div key={member.id} className="bg-white rounded-2xl overflow-hidden">
                    {/* Photo strip */}
                    <div className="h-56 overflow-hidden relative">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                      <div className="absolute bottom-4 left-5">
                        <p className="text-white font-bold text-base leading-tight" translate="no" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {member.name}
                        </p>
                        <span className="inline-block mt-1 bg-[#6B7F5C] text-white text-xs font-semibold px-2.5 py-0.5 rounded-full">
                          {member.role}
                        </span>
                      </div>
                    </div>

                    {/* Areas */}
                    <div className="p-6">
                      <p className="text-xs font-semibold uppercase tracking-widest text-[#4A4A4A] mb-3">
                        Áreas que gestiona
                      </p>
                      <ul className="space-y-2 mb-5">
                        {areas.map((area) => (
                          <li key={area} className="flex items-center gap-2 text-sm text-[#4A4A4A]">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className="ri-check-line text-[#6B7F5C]" />
                            </div>
                            {area}
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/equipo"
                        className="flex items-center gap-1.5 text-[#6B7F5C] text-sm font-medium hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap"
                      >
                        Ver perfil completo
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-right-line" />
                        </div>
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            <div className="text-center mb-14">
              <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-3">Cómo trabajamos</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Nuestro proceso, paso a paso
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "01", title: "Consulta inicial gratuita", desc: "Analizamos tu situación sin compromiso para entender tus necesidades.", icon: "ri-phone-line" },
                { step: "02", title: "Propuesta personalizada", desc: "Diseñamos un plan de servicios a medida con honorarios claros.", icon: "ri-file-text-line" },
                { step: "03", title: "Gestión y seguimiento", desc: "Nos encargamos de todo y te mantenemos informado en todo momento.", icon: "ri-settings-3-line" },
                { step: "04", title: "Resultados y optimización", desc: "Revisamos periódicamente para mejorar y adaptarnos a cambios normativos.", icon: "ri-line-chart-line" },
              ].map((item, i) => (
                <div key={i} className="bg-[#F8F7F4] rounded-2xl p-6 relative">
                  <span className="text-6xl font-bold text-[#EAE9E5] absolute top-4 right-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {item.step}
                  </span>
                  <div className="w-11 h-11 bg-[#6B7F5C]/10 rounded-xl flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-[#6B7F5C] text-xl`} />
                  </div>
                  <h3 className="font-bold text-[#1A1A1A] mb-2 text-sm">{item.title}</h3>
                  <p className="text-[#6B6B6B] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#6B7F5C]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              ¿Listo para empezar?
            </h2>
            <p className="text-white/80 mb-8 text-base">
              Contáctanos hoy y te asignamos el asesor más adecuado para ti sin ningún compromiso.
            </p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-white text-[#6B7F5C] font-semibold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Contactar ahora
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
}
